import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router';
import { Box, Typography } from '@mui/material';
import { getFadeDownGradient } from 'lib/helpers';
import { useLocalHistory } from 'lib/useLocalHistory';
import { ROUTES } from 'pages/routes';
import { setBasket } from 'store/basket/basketActions';
import { ButtonV2 } from 'ui/ButtonV2';
import { PayCheckIcon } from './PayCheckIcon';
import { REDUCED_CONTENT_MAX_WIDTH } from 'config/constants';
import { isString } from 'lib/typeInference';

const PREFIX = 'PayGuestConversion';

const classes = {
    buttonsRoot: `${PREFIX}buttonsRoot`,
    placeholder: `${PREFIX}placeholder`,
    title: `${PREFIX}title`,
    textRow: `${PREFIX}textRow`,
    icon: `${PREFIX}icon`,
    text: `${PREFIX}text`,
    button: `${PREFIX}button`,
    link: `${PREFIX}link`,
    signInText: `${PREFIX}signInText`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.buttonsRoot}`]: {
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: REDUCED_CONTENT_MAX_WIDTH,
        right: 0,
        padding: theme.spacing(1),
        paddingTop: theme.spacing(2),
        background: getFadeDownGradient(theme.palette.background.default, [0, 0.9, 1], ['0%', '50%', ' 100%'])
    },

    [`& .${classes.placeholder}`]: {
        width: '100%'
    },

    [`& .${classes.title}`]: {
        fontWeight: 700,
        fontSize: '18px'
    },

    [`& .${classes.textRow}`]: {
        display: 'flex',
        alignItems: 'flex-start',
        marginTop: theme.spacing(2),
        paddingLeft: theme.spacing(0.5)
    },

    [`& .${classes.icon}`]: {
        height: '24px',
        width: '24px',
        marginRight: theme.spacing(1),
        marginTop: theme.spacing(0.25)
    },

    [`& .${classes.text}`]: {
        fontWeight: 400,
        fontSize: '16px'
    },

    [`& .${classes.button}`]: {
        marginBottom: theme.spacing(1)
    },

    [`& .${classes.link}`]: {
        textDecoration: 'none',
        fontWeight: 700,
        color: theme.palette.primary.main
    },

    [`& .${classes.signInText}`]: {
        fontSize: '16px'
    }
}));

interface PayGuestConversionProps {
    locationId: string;
    trackMyOrderLink?: string;
}

export const PayGuestConversion: React.FC<React.PropsWithChildren<PayGuestConversionProps>> = ({
    locationId,
    trackMyOrderLink
}) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const [height, setHeight] = React.useState(0);
    const ref = React.useRef<HTMLDivElement>(null);
    const { getParsedPath, push } = useLocalHistory();
    const handleClick = React.useCallback(() => {
        setBasket({ locationId })(dispatch);
        push(ROUTES.USER.REGISTER);
    }, [dispatch, locationId, push]);
    React.useEffect(() => {
        const rect = ref.current?.getBoundingClientRect();
        if (rect && rect.height !== height) {
            setHeight(rect.height);
        }
    }, [height]);
    return (
        <StyledBox>
            <Box px={2} pt={3.5}>
                <Typography className={classes.title}>{t('PAT_QUICKPAY_GUEST_CONVERSION_TITLE')}</Typography>
                {t('PAT_QUICKPAY_GUEST_CONVERSION_CONTENT')
                    .split('\n')
                    .map((item: string, index: number) => (
                        <div className={classes.textRow} key={`guest-conversion-${index}`}>
                            <PayCheckIcon className={classes.icon} />
                            <Typography className={classes.text}>{item}</Typography>
                        </div>
                    ))}
            </Box>
            <div className={classes.buttonsRoot} ref={ref}>
                {isString(trackMyOrderLink) && (
                    <a href={trackMyOrderLink} target="_blank" rel="noreferrer">
                        <ButtonV2 color="primary" variant="outlined">
                            {t('DELIVERY_TRACK_ORDER')}
                        </ButtonV2>
                    </a>
                )}
                <ButtonV2 onClick={handleClick} className={classes.button}>
                    {t('ORDER_STATUS_GUEST_CONVERT')}
                </ButtonV2>
                <Typography className={classes.signInText} align="center">
                    {t('AUTH_ALREADY_HAVE_ACCOUNT') + ' '}
                    <Link className={classes.link} to={getParsedPath(ROUTES.USER.LOGIN)}>
                        {t('ONBOARDING_SIGN_IN')}
                    </Link>
                </Typography>
            </div>
            <div className={classes.placeholder} style={{ height }} />
        </StyledBox>
    );
};
